import type { WerfCard } from '@/api/warehouse';
import { systemPillClass, systemPillLabel, initialsOf, formatDayLabel } from '@/utils/warehouse';

interface WarehouseWerfCardProps {
  werf: WerfCard;
  onOpen: (werf: WerfCard) => void;
  /** Search mode spans all days → group by fase and list its planning days underneath
   *  (instead of the single-day "Dag x/y" badge that's meaningless across dates). */
  searchMode?: boolean;
}

/** Read-only werf card for the magazijnier day-werven list. Matches mockup screen 1. */
export function WarehouseWerfCard({ werf, onOpen, searchMode = false }: WarehouseWerfCardProps) {
  const hasAggregate = werf.collectorCount > 0 || werf.systems.length > 0;
  const subtitle = [werf.werfAddress, werf.saleOrderName].filter(Boolean).join(' · ');

  return (
    <button
      type="button"
      onClick={() => onOpen(werf)}
      className="w-full text-left rounded-2xl bg-white p-3.5 shadow-sm border border-gray-100 hover:border-td-blue/30 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-bold text-td-navy text-sm truncate">{werf.projectName}</h3>
          {werf.faseName && (
            <p className="text-xs font-semibold text-td-blue truncate mt-0.5">{werf.faseName}</p>
          )}
          {subtitle && (
            <p className="text-xs text-td-muted truncate flex items-center gap-1 mt-0.5">
              <svg className="w-3 h-3 text-td-light shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              {subtitle}
            </p>
          )}
        </div>
        <svg className="w-4 h-4 text-td-light shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
          <path d="m9 18 6-6-6-6" />
        </svg>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {!searchMode && werf.dag && werf.dag.total > 1 && (
          <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-td-navy/10 text-td-navy">
            Dag {werf.dag.index}/{werf.dag.total}
          </span>
        )}

        {hasAggregate ? (
          <>
            {werf.systems.map((s) => (
              <span
                key={s.systeem}
                className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${systemPillClass(s.systeem)}`}
              >
                {systemPillLabel(s)}
              </span>
            ))}
            <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gray-100 text-td-muted">
              {werf.collectorCount} {werf.collectorCount === 1 ? 'collector' : 'collectoren'}
            </span>
          </>
        ) : (
          <>
            <span className="px-2 py-0.5 text-[10px] font-bold rounded-full bg-amber-100 text-amber-800">
              Nog niet opgelijst
            </span>
            <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-gray-100 text-td-muted">
              geen collectoren
            </span>
          </>
        )}
      </div>

      {werf.installer ? (
        <div className="mt-2.5 flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-td-green/15 flex items-center justify-center shrink-0">
            <span className="text-[9px] font-bold text-td-green">{initialsOf(werf.installer.name)}</span>
          </div>
          <span className="text-[11px] text-td-muted truncate">{werf.installer.name}</span>
        </div>
      ) : (
        <div className="mt-2 text-[11px] text-td-light italic">
          Installateur niet toegewezen · enkel taak &amp; offerte beschikbaar
        </div>
      )}

      {searchMode && werf.days.length > 0 && (
        <div className="mt-2.5 pt-2.5 border-t border-gray-50">
          <p className="text-[10px] font-bold uppercase tracking-wide text-td-muted mb-1.5">
            Geplande dagen ({werf.days.length})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {werf.days.map((d) => (
              <span
                key={d.planningTaskId}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-td-blue/10 text-td-blue"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
                {formatDayLabel(d.date)}
              </span>
            ))}
          </div>
        </div>
      )}
    </button>
  );
}
